import i18n from '../../i18n';
import { BaseApiClient } from './base-client';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * Types for Motion Control API
 */

export interface MotionControlAttachment {
  id: string;
  userId: string;
  type: 'image' | 'video';
  s3Key: string;
  s3Url: string;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
  duration?: number;
  width?: number;
  height?: number;
  createdAt: string;
  updatedAt: string;
}

export interface MotionControlAttachmentsResponse {
  image: MotionControlAttachment | null;
  video: MotionControlAttachment | null;
}

export interface MotionControlGenerateResponse {
  id: string;
  jobId?: string;
  status: 'queued' | 'in_progress' | 'generating' | 'completed' | 'error';
  tokensUsed?: number;
  mode?: 'standard' | 'pro';
  prompt?: string;
  originalPrompt?: string;
  wasTranslated?: boolean;
  statusUrl?: string;
  cancelUrl?: string;
  error?: string;
  tokensRefunded?: number;
  newBalance?: number;
}

export interface MotionControlPollResponse {
  id: string;
  status: 'queued' | 'generating' | 'completed' | 'error';
  video?: {
    id?: string;
    url: string;
    duration?: number;
  };
  error?: string;
  warning?: string;
  tokensRefunded?: boolean;
}

export interface MotionControlHistoryVideo {
  id: string;
  url: string;
  thumbnail?: string;
  prompt?: string;
  duration?: number;
  status: 'completed' | 'failed' | 'error';
  errorMessage?: string;
  mode: 'standard' | 'pro';
  keepAudio: boolean;
  createdAt: string;
}

export interface MotionControlHistoryResponse {
  videos: MotionControlHistoryVideo[];
  hasMore: boolean;
  offset: number;
  limit: number;
}

export interface ActiveMotionControlGeneration {
  id: string;
  jobId?: string | null;
  status: 'queued' | 'generating' | 'completed' | 'error';
  prompt?: string;
  mode?: 'standard' | 'pro';
  createdAt?: string;
  errorMessage?: string;
  userName?: string;
  userPhoto?: string;
  isTeamMember?: boolean;
}

/**
 * Motion Control API - methods for AI motion control using Kling via Higgsfield
 * Transfer motion from reference video to static image
 */
export class MotionControlApi extends BaseApiClient {
  
  // ===== ATTACHMENTS API =====
  
  /**
   * Get all saved attachments for user
   * Returns image and video
   */
  async getAttachments(): Promise<MotionControlAttachmentsResponse> {
    try {
      const response = await this.client.get('/api/motion-control/attachments');
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching attachments:', error);
      return {
        image: null,
        video: null
      };
    }
  }

  /**
   * Upload and save an attachment (image or video)
   * @param file - File to upload
   * @param type - Type of attachment ('image' or 'video')
   * @param options - Additional options
   */
  async uploadAttachment(
    file: File,
    type: 'image' | 'video',
    options?: {
      duration?: number;
      onProgress?: (progress: number) => void;
    }
  ): Promise<{ attachment: MotionControlAttachment }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    
    if (options?.duration !== undefined) {
      formData.append('duration', options.duration.toString());
    }

    const response = await this.client.post('/api/motion-control/attachments', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (options?.onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          options.onProgress(progress);
        }
      },
    });
    return response.data.data;
  }

  /**
   * Delete an attachment
   */
  async deleteAttachment(attachmentId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/motion-control/attachments/${attachmentId}`);
    return response.data;
  }

  /**
   * Clear all attachments
   */
  async clearAllAttachments(): Promise<{ message: string }> {
    const response = await this.client.delete('/api/motion-control/attachments/clear/all');
    return response.data;
  }

  // ===== GENERATION API =====

  /**
   * Generate motion-controlled video
   * 
   * @param data - Generation parameters
   */
  async generate(data: {
    image_url: string;
    video_url: string;
    mode?: 'standard' | 'pro';
    model_name?: 'kling-v2-6' | 'kling-v3';
    keep_audio?: boolean;
    prompt?: string;
    autoTranslate?: boolean;
    character_orientation?: 'video' | 'image';
    teamId?: string;
    folderId?: string;
    trim_start?: number;
    trim_end?: number;
  }): Promise<MotionControlGenerateResponse> {
    try {
      const teamId = sanitizeTeamId(data.teamId);
      const response = await this.client.post('/api/motion-control/generate', { ...data, teamId });
      return response.data.data;
    } catch (error: any) {
      // Re-throw with normalized error structure
      const errorData = error.response?.data;
      const errorMessage = typeof errorData?.error === 'string' 
        ? errorData.error 
        : errorData?.message || error.message || i18n.t('common:error.videoGeneration');
      
      throw {
        ...error,
        response: {
          ...error.response,
          status: error.response?.status || 500,
          data: {
            error: errorMessage,
            tokensRefunded: errorData?.tokensRefunded
          }
        }
      };
    }
  }

  /**
   * Calculate token cost for generation
   */
  async calculatePrice(mode: 'standard' | 'pro', duration: number): Promise<{
    mode: string;
    duration: number;
    pricePerSecond: number;
    tokens: number;
  }> {
    const response = await this.client.post('/api/motion-control/generate/calculate-price', {
      mode,
      duration
    });
    return response.data.data;
  }

  /**
   * Poll generation status
   * Returns current status and video URL when completed
   */
  async pollStatus(generationId: string): Promise<MotionControlPollResponse> {
    try {
      const response = await this.client.get('/api/motion-control/status', {
        params: { generation_id: generationId },
      });
      return response.data.data;
    } catch (error: any) {
      // Handle API errors gracefully
      if (error.response?.data) {
        const errorData = error.response.data;
        
        const errorMessage = typeof errorData.error === 'string' 
          ? errorData.error 
          : errorData.message || i18n.t('common:error.statusCheck');
        
        return {
          id: generationId,
          status: 'error',
          error: errorMessage
        };
      }
      
      return {
        id: generationId,
        status: 'error',
        error: error.message || i18n.t('common:error.network')
      };
    }
  }

  /**
   * Get active motion-control generations
   */
  async getActiveGenerations(rawTeamId?: string): Promise<{
    success: boolean;
    data?: {
      generations: ActiveMotionControlGeneration[];
      count: number;
      isTeamMode?: boolean;
    };
    error?: string;
  }> {
    const teamId = sanitizeTeamId(rawTeamId);
    try {
      const params: Record<string, string> = {};
      if (teamId) params.teamId = teamId;
      
      const response = await this.client.get('/api/motion-control/status/active', { params });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching active generations:', error);
      return {
        success: false,
        data: {
          generations: [],
          count: 0,
          isTeamMode: !!teamId
        },
        error: error.response?.data?.error || error.message || i18n.t('common:error.activeGenerationsLoad')
      };
    }
  }

  /**
   * Cancel a generation
   */
  async cancelGeneration(generationId: string): Promise<{
    success: boolean;
    message?: string;
    tokensRefunded?: boolean;
    error?: string;
  }> {
    try {
      const response = await this.client.post('/api/motion-control/status/cancel', {
        generation_id: generationId
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || i18n.t('common:error.generationCancel')
      };
    }
  }

  /**
   * Clear stuck motion-control generations (NO token refund)
   */
  async clearStuckGenerations(clearAll = false): Promise<{
    success: boolean;
    data?: {
      cleared: number;
      generationIds: string[];
    };
    error?: string;
  }> {
    const response = await this.client.post('/api/motion-control/status/clear', { clearAll });
    return response.data;
  }

  // ===== HISTORY API =====

  /**
   * Get generation history
   */
  async getHistory(params: {
    teamId?: string;
    offset?: number;
    limit?: number;
  }): Promise<MotionControlHistoryResponse> {
    try {
      const sanitizedParams = { ...params, teamId: sanitizeTeamId(params.teamId) };
      const response = await this.client.get('/api/motion-control/history', { params: sanitizedParams });
      return response.data.data;
    } catch (error: any) {
      console.error('Error fetching history:', error);
      return {
        videos: [],
        hasMore: false,
        offset: params.offset || 0,
        limit: params.limit || 20
      };
    }
  }

  /**
   * Delete a generation from history
   */
  async deleteGeneration(generationId: string): Promise<{ message: string }> {
    const response = await this.client.delete(`/api/motion-control/${generationId}`);
    return response.data;
  }
}

// Export singleton instance
export const motionControlApi = new MotionControlApi();
